import type { CreateEventRequest } from '@pulsify/shared/dto'
import { normalizeCountry } from '~/shared/lib/countries'

interface FormLocation {
  name?: string | null
  street: string
  city: string
  country: string
  coordinates?: { lat: number; lng: number }
}

interface AddressParts {
  street: string
  city: string
  country: string
}

function sameAddress(a: AddressParts, b: AddressParts): boolean {
  return (
    a.street === b.street &&
    a.city === b.city &&
    normalizeCountry(a.country) === normalizeCountry(b.country)
  )
}

/** The request's place: what the form says, with a pin only for a full address. */
export function eventLocationRequest(
  location: FormLocation,
  pending: boolean,
): NonNullable<CreateEventRequest['location']> {
  return {
    name: location.name || null,
    street: location.street || undefined,
    city: location.city || undefined,
    country: location.country || undefined,
    ...(!pending && location.coordinates ? { coordinates: location.coordinates } : {}),
  }
}

/**
 * The place fields of a new event. A venue announced later needs only the city and is never
 * tied to a listing; a linked listing's own address is left for the server to copy.
 */
export function createEventPlace(
  data: { location: FormLocation; locationPending?: boolean; listingId?: string | null },
  listingLocation?: AddressParts | null,
): Pick<CreateEventRequest, 'location' | 'locationPending' | 'listingId'> {
  const { location } = data
  if (data.locationPending) {
    const hasCity = !!(location.city && location.country)
    return {
      listingId: null,
      locationPending: true,
      ...(hasCity ? { location: eventLocationRequest(location, true) } : {}),
    }
  }

  const listingId = data.listingId || null
  const hasAddress = !!(location.street && location.city && location.country)
  const sendLocation =
    hasAddress && (!listingId || (listingLocation && !sameAddress(location, listingLocation)))
  return {
    listingId,
    ...(sendLocation ? { location: eventLocationRequest(location, false) } : {}),
  }
}
